import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatDateTime, formatNOK } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Save, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { TagEditor } from "@/components/TagEditor";
import { PRIMARY_BTN_CLS } from "@/components/premium";

const STATUS_LABEL = { booked: "Booket", completed: "Fullført", no_show: "Ikke møtt", cancelled: "Avlyst" };

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState({ name: "", phone: "", email: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteBookings, setDeleteBookings] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/customers/${id}`);
      setData(res.data);
      setEdit({
        name: res.data.customer.name, phone: res.data.customer.phone || "",
        email: res.data.customer.email || "", notes: res.data.customer.notes || "",
      });
    } catch { toast.error("Kunne ikke laste kunde"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (edit.name?.trim()) payload.name = edit.name.trim();
      // Allow explicit clearing (empty) for optional fields by sending null-equivalent
      payload.phone = edit.phone?.trim() || null;
      payload.email = edit.email?.trim() || null;
      payload.notes = edit.notes ?? "";
      await api.put(`/customers/${id}`, payload);
      toast.success("Lagret");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail.some((d) => String(d?.loc || []).includes("email"))) {
        toast.error("Ugyldig e-postadresse");
      } else {
        toast.error(typeof detail === "string" ? detail : "Kunne ikke lagre");
      }
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const res = await api.delete(`/customers/${id}`, {
        params: deleteBookings ? { delete_bookings: true } : {},
      });
      const removed = res.data?.bookings_removed || 0;
      toast.success(removed > 0 ? `Kunde og ${removed} booking(er) slettet` : "Kunde slettet");
      setConfirmOpen(false);
      navigate("/kunder");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke slette");
    } finally { setDeleting(false); }
  };

  if (!data) return (
    <div className="space-y-6 anim-in">
      <div className="h-4 w-20 bg-primary/10 rounded animate-pulse" />
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-2xl bg-primary/10 animate-pulse" />
        <div className="flex-1 space-y-2">
          <div className="h-8 w-48 bg-primary/10 rounded animate-pulse" />
          <div className="h-4 w-64 bg-primary/10 rounded animate-pulse" />
        </div>
      </div>
      <div className="rounded-2xl border border-border p-6 space-y-3">
        <div className="h-5 w-24 bg-primary/10 rounded animate-pulse" />
        <div className="h-4 w-full bg-primary/10 rounded animate-pulse" />
        <div className="h-4 w-3/4 bg-primary/10 rounded animate-pulse" />
      </div>
    </div>
  );
  const { customer, bookings } = data;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <Link to="/kunder" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Tilbake
      </Link>

      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center text-primary font-heading font-bold text-2xl">
          {customer.name.trim().split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="flex-1">
          <h1 className="font-heading text-3xl font-bold tracking-tight">{customer.name}</h1>
          <p className="text-muted-foreground">{customer.phone} {customer.email && `· ${customer.email}`}</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground items-center">
            <Badge variant="outline" className="rounded-full">Besøk: {customer.total_visits}</Badge>
            {customer.next_booking && <Badge variant="outline" className="rounded-full">Neste: {formatDateTime(customer.next_booking)}</Badge>}
            <Badge
              variant="outline"
              className="rounded-full"
              data-testid="customer-last-contacted"
            >
              Sist kontaktet: {customer.last_contacted_at
                ? new Date(customer.last_contacted_at).toLocaleDateString("nb-NO", { dateStyle: "medium" })
                : "Ikke registrert"}
            </Badge>
          </div>
        </div>
        <Button
          onClick={() => navigate("/ai-meldinger", { state: { customerId: customer.id } })}
          className={`${PRIMARY_BTN_CLS} hidden sm:inline-flex`}
          data-testid="customer-followup-btn"
        >
          <Sparkles className="w-4 h-4" strokeWidth={2} /> Følg opp via AI
        </Button>
      </div>
      <Button
        onClick={() => navigate("/ai-meldinger", { state: { customerId: customer.id } })}
        className={`w-full ${PRIMARY_BTN_CLS} sm:hidden h-11`}
        data-testid="customer-followup-btn-mobile"
      >
        <Sparkles className="w-4 h-4" strokeWidth={2} /> Følg opp via AI
      </Button>

      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl border-border lg:col-span-2">
          <CardHeader><CardTitle className="font-heading">Historikk</CardTitle></CardHeader>
          <CardContent>
            {bookings.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">Ingen bookinger enda.</p>
            ) : (
              <ul className="divide-y divide-border">
                {bookings.map((b) => (
                  <li key={b.id} className="py-3 flex items-center justify-between" data-testid="history-item">
                    <div className="min-w-0">
                      <p className="font-semibold truncate">{b.service_name}</p>
                      <p className="text-sm text-muted-foreground">{formatDateTime(b.start_at)}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="font-semibold">{formatNOK(b.price_nok)}</p>
                      <Badge variant="outline" className="rounded-full text-xs">{STATUS_LABEL[b.status]}</Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border-border">
          <CardHeader><CardTitle className="font-heading">Rediger</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><Label>Navn</Label><Input className="rounded-xl mt-1.5" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-name" /></div>
            <div><Label>Telefon</Label><Input className="rounded-xl mt-1.5" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} data-testid="edit-phone" /></div>
            <div><Label>E-post</Label><Input type="email" className="rounded-xl mt-1.5" value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="edit-email" /></div>
            <div><Label>Notater</Label><Textarea className="rounded-xl mt-1.5" rows={4} value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} data-testid="edit-notes" /></div>
            <TagEditor
              customerId={customer.id}
              initialTags={customer.tags || []}
              onChange={(next) =>
                setData((prev) =>
                  prev ? { ...prev, customer: { ...prev.customer, tags: next } } : prev
                )
              }
            />
            <Button onClick={save} disabled={saving} className={`w-full ${PRIMARY_BTN_CLS} h-11`} data-testid="save-customer-btn">
              <Save className="w-4 h-4" strokeWidth={2} /> {saving ? "Lagrer..." : "Lagre endringer"}
            </Button>
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => { setDeleteBookings(false); setConfirmOpen(true); }}
                className="w-full rounded-xl border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                data-testid="delete-customer-detail-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Slett kunde
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="rounded-2xl" data-testid="detail-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Slett {customer.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Kunden vil bli fjernet fra registeret.
              {bookings.length > 0 && (
                <span className="block mt-2">
                  Denne kunden har <strong>{bookings.length} booking(er)</strong> i historikken.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3">
            <Checkbox
              id="detail-delete-bookings"
              checked={deleteBookings}
              onCheckedChange={(v) => setDeleteBookings(!!v)}
              data-testid="detail-delete-bookings-checkbox"
            />
            <div className="grid gap-0.5 leading-tight">
              <Label htmlFor="detail-delete-bookings" className="cursor-pointer text-sm">
                Slett også all booking-historikk
              </Label>
              <p className="text-xs text-muted-foreground">
                Standard: bookinger beholdes med navn intakt.
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl" data-testid="detail-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleting}
              className="rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="detail-delete-confirm"
            >
              {deleting ? "Sletter..." : "Slett kunde"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
